import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Firestore imports removed
import { ApiService } from '../../../services/api_service';
import { formatFirestoreDate } from '../../../utils/date_formatter';

const SearchComponent = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    category: '',
    location: '',
    eventType: '',
    priceRange: { min: '', max: '' },
    dateRange: { start: '', end: '' }
  });

  // Available filter options
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    locations: [],
    eventTypes: []
  });

  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close on click outside for desktop (lg and above)
      if (window.innerWidth >= 1024 && searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    if (searchTerm.length > 2) {
      searchEvents();
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchTerm, filters]);

  // Load filter options when component mounts
  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const response = await ApiService.getAllEvents();
      let allEvents = response.data || response;
      if (allEvents && !Array.isArray(allEvents) && typeof allEvents === 'object') {
        allEvents = Object.values(allEvents);
      }
      if (!Array.isArray(allEvents)) allEvents = [];

      allEvents = allEvents.filter(doc => doc.showInApp === true || doc.showInApp === null || doc.showInApp === undefined);

      // Extract unique categories, locations, and event types
      const categories = [...new Set(allEvents.map(event => event.category).filter(Boolean))];
      const locations = [...new Set(allEvents.map(event => event.eventLocation?.locationText).filter(Boolean))];
      const eventTypes = [...new Set(allEvents.map(event => event.eventType).filter(Boolean))];

      setFilterOptions({
        categories: categories.sort(),
        locations: locations.sort(),
        eventTypes: eventTypes.sort()
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const searchEvents = async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.getAllEvents();
      let allEvents = response.data || response;
      if (allEvents && !Array.isArray(allEvents) && typeof allEvents === 'object') {
        allEvents = Object.values(allEvents);
      }
      if (!Array.isArray(allEvents)) allEvents = [];

      // Pre-filter for showInApp
      allEvents = allEvents.filter(doc => doc.showInApp === true || doc.showInApp === null || doc.showInApp === undefined);

      // Sort by startTime desc
      allEvents.sort((a, b) => {
        const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return dateB - dateA;
      });

      // Filter events based on search term and filters
      let filteredEvents = allEvents.filter(event => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
          event.name?.toLowerCase().includes(searchLower) ||
          event.eventLocation?.locationText?.toLowerCase().includes(searchLower) ||
          event.description?.toLowerCase().includes(searchLower) ||
          event.category?.toLowerCase().includes(searchLower) ||
          event.eventType?.toLowerCase().includes(searchLower)
        );

        if (!matchesSearch) return false;

        // Apply additional filters
        if (filters.category && event.category !== filters.category) return false;
        if (filters.location && event.eventLocation?.locationText !== filters.location) return false;
        if (filters.eventType && event.eventType !== filters.eventType) return false;

        // Date range filter
        if (filters.dateRange.start || filters.dateRange.end) {
          const eventDate = new Date(event.startTime);
          if (filters.dateRange.start && eventDate < new Date(filters.dateRange.start)) return false;
          if (filters.dateRange.end && eventDate > new Date(filters.dateRange.end)) return false;
        }

        // Price range filter
        if (filters.priceRange.min || filters.priceRange.max) {
          const minPrice = getMinimumTicketPriceValue(event);
          if (filters.priceRange.min && minPrice < parseFloat(filters.priceRange.min)) return false;
          if (filters.priceRange.max && minPrice > parseFloat(filters.priceRange.max)) return false;
        }

        return true;
      });

      // Limit to 50
      filteredEvents = filteredEvents.slice(0, 50);

      setSearchResults(filteredEvents);
      setShowResults(filteredEvents.length > 0);
    } catch (error) {
      console.error('Error searching events:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showResults) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleEventClick(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        onClose();
        break;
    }
  };

  const handleEventClick = (event) => {
    navigate(`/events/${event.id}`);
    setShowResults(false);
    onClose();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
  };

  const getMinimumTicketPrice = (event) => {
    if (!event || !Array.isArray(event.tickets) || event.tickets.length === 0) {
      return 'Price TBA';
    }

    const allPrices = [];

    event.tickets.forEach((ticketDate) => {
      if (ticketDate.slots && Array.isArray(ticketDate.slots)) {
        ticketDate.slots.forEach((slot) => {
          if (slot.tickets && Array.isArray(slot.tickets)) {
            slot.tickets.forEach((ticket) => {
              if (ticket.price !== undefined && ticket.price !== null) {
                allPrices.push(ticket.price);
              }
            });
          }
        });
      }
    });

    return allPrices.length > 0 ? `₹${Math.min(...allPrices)}` : 'Price TBA';
  };

  const getMinimumTicketPriceValue = (event) => {
    if (!event || !Array.isArray(event.tickets) || event.tickets.length === 0) {
      return 0;
    }

    const allPrices = [];

    event.tickets.forEach((ticketDate) => {
      if (ticketDate.slots && Array.isArray(ticketDate.slots)) {
        ticketDate.slots.forEach((slot) => {
          if (slot.tickets && Array.isArray(slot.tickets)) {
            slot.tickets.forEach((ticket) => {
              if (ticket.price !== undefined && ticket.price !== null) {
                allPrices.push(ticket.price);
              }
            });
          }
        });
      }
    });

    return allPrices.length > 0 ? Math.min(...allPrices) : 0;
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handlePriceRangeChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [type]: value
      }
    }));
  };

  const handleDateRangeChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: value
      }
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      location: '',
      eventType: '',
      priceRange: { min: '', max: '' },
      dateRange: { start: '', end: '' }
    });
  };

  const hasActiveFilters = () => {
    return filters.category || filters.location || filters.eventType ||
      filters.priceRange.min || filters.priceRange.max ||
      filters.dateRange.start || filters.dateRange.end;
  };

  return (
    <div ref={searchRef} className="relative w-full">
      {/* Search Input */}
      <div className="flex w-full max-w-[420px] h-[44px] px-4 sm:px-5 py-3 items-center gap-3 rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 hover:border-[#17BD3B]/50 transition-all duration-300 focus-within:border-[#17BD3B] focus-within:shadow-lg focus-within:shadow-[#17BD3B]/20 mx-auto">
        <svg className="w-5 h-5 text-gray-400 flex-shrink-0 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          className="flex-1 bg-transparent border-none text-white text-sm outline-none placeholder:text-gray-400 font-medium"
          placeholder="Search for Events, Workshop"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <div className="flex items-center gap-2">
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="p-1 text-gray-400 hover:text-white transition-colors duration-200"
              title="Clear filters"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-1 transition-colors duration-200 ${showFilters ? 'text-[#17BD3B]' : 'text-gray-400 hover:text-white'
              }`}
            title="Toggle filters"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
          </button>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-[#17BD3B] border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-2xl z-50 mx-auto max-w-[420px] p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-sm font-medium">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-xs text-gray-400 hover:text-white transition-colors duration-200"
              >
                Clear All
              </button>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
              >
                <option value="">All Categories</option>
                {filterOptions.categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Location</label>
              <select
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
              >
                <option value="">All Locations</option>
                {filterOptions.locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            {/* Event Type Filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Event Type</label>
              <select
                value={filters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
              >
                <option value="">All Types</option>
                {filterOptions.eventTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Price Range Filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Price Range (₹)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.priceRange.min}
                  onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.priceRange.max}
                  onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
                />
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-[#17BD3B] transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Results Dropdown */}
      {showResults && searchResults.length > 0 && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-50 mx-auto max-w-[420px]"
        >
          <div className="p-2">
            <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-700/50">
              {searchResults.length} event{searchResults.length !== 1 ? 's' : ''} found
            </div>
            {searchResults.map((event, index) => (
              <div
                key={event.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${selectedIndex === index
                  ? 'bg-[#17BD3B]/20 border border-[#17BD3B]/30'
                  : 'hover:bg-gray-800/50'
                  }`}
                onClick={() => handleEventClick(event)}
              >
                {/* Event Image */}
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={event.poster?.verticalUrl || event.poster?.horizontalUrl || event.imagesUrls?.[0] || '/default-event.jpg'}
                    alt={event.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0yNCAzNkMzMC42Mjc0IDM2IDM2IDMwLjYyNzQgMzYgMjRDMzYgMTcuMzcyNiAzMC42Mjc0IDEyIDI0IDEyQzE3LjM3MjYgMTIgMTIgMTcuMzcyNiAxMiAyNEMxMiAzMC42Mjc0IDE3LjM3MjYgMzYgMjQgMzZaIiBmaWxsPSIjNjY2Ii8+Cjwvc3ZnPgo=';
                    }}
                  />
                </div>

                {/* Event Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-sm sm:text-base truncate">
                    {event.name}
                  </h4>
                  <p className="text-gray-400 text-xs sm:text-sm truncate">
                    {formatFirestoreDate(event.startTime)} • {event.eventLocation?.locationText}
                  </p>
                  <p className="text-[#17BD3B] text-xs sm:text-sm font-medium">
                    {getMinimumTicketPrice(event)}
                  </p>
                </div>

                {/* Arrow Icon */}
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {showResults && searchResults.length === 0 && searchTerm.length > 2 && !isLoading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-2xl p-4 z-50 mx-auto max-w-[420px]">
          <div className="text-center text-gray-400">
            <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-sm">No events found for "{searchTerm}"</p>
            <p className="text-xs mt-1">Try different keywords or check spelling</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;

